/**
 * Tela /products (TH-53223 / M2.E8.S2.1).
 *
 * Lista paginada server-side de products do tenant. Substitui o PageStub
 * registrado em router.tsx até esta subtask.
 *
 * Backend: GET /api/products (TH-53210 / M2.E8.S1.1) com filtros opcionais
 *   - `q` busca em name/sku/ean (ILIKE)
 *   - `area` (UUID — não confundir com `area_id`) filtra por área
 *   - paginação `page`/`per_page` retornando `{ data, meta:{page,per_page,total} }`.
 *
 * Decisões:
 * - Preço atual: ProductRead não inclui preço (vive em `store_prices`,
 *   por loja). Listagem mostra "—" — preço completo aparece no detail.
 * - Coluna Área: usa nome via /api/areas (mapa id→name). Se /api/areas
 *   falhar (404/500), coluna mostra "—" e FilterPills é omitido.
 * - Botão "+ Novo produto": disabled até a tela de criação existir
 *   (sem subtask aberta no plano M2.E8 ainda).
 * - Click na row navega para /products/$productId (rota stub adicionada
 *   em router.tsx — TH-53224 implementa o detalhe real).
 *
 * Refs visuais: StoresPage (server pagination + Link em row + DataTable).
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Upload } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { ProductsImportModal } from "@/components/ProductsImportModal";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import {
  FilterPills,
  type FilterPillOption,
} from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { useAuth } from "@/hooks/useAuth";

interface Product {
  id: string;
  sku: string | null;
  ean: string | null;
  name: string;
  description: string | null;
  unit: string | null;
  image_url: string | null;
  ai_hint: string | null;
  area_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface PagedResponse {
  data: Product[];
  meta: { page: number; per_page: number; total: number };
}

interface Area {
  id: string;
  name: string;
  slug: string;
}

interface AreasResponse {
  data: Area[];
  meta: { total: number };
}

const PER_PAGE = 20;
const ALL_AREAS = "__all__";

async function fetchProducts(
  page: number,
  q: string,
  areaId: string,
): Promise<PagedResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(PER_PAGE),
  });
  if (q.trim()) params.set("q", q.trim());
  if (areaId !== ALL_AREAS) params.set("area", areaId);
  const res = await fetch(`/api/products?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedResponse;
}

async function fetchAreas(): Promise<AreasResponse> {
  // per_page alto: tenant raramente tem >100 áreas; pega tudo de uma vez
  // pra preencher FilterPills + mapa id→name sem paginar.
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AreasResponse;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ProductsPage() {
  const { user } = useAuth();
  const canRead =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "operator" ||
    user?.role === "catalog_operator";

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [areaId, setAreaId] = useState<string>(ALL_AREAS);
  const [importOpen, setImportOpen] = useState(false);

  const canImport =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "operator" ||
    user?.role === "catalog_operator";

  // Debounce 300ms — evita disparar query a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page quando filtro muda (q já reseta dentro do debounce).
  useEffect(() => {
    setPage(1);
  }, [areaId]);

  const productsQuery = useQuery({
    queryKey: ["products", page, q, areaId],
    queryFn: () => fetchProducts(page, q, areaId),
    staleTime: 30_000,
    enabled: canRead,
  });

  // Áreas — opcional. Se 401/403/404/500, ignora e omite FilterPills.
  const areasQuery = useQuery({
    queryKey: ["areas", "for-products-filter"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: canRead,
    retry: false,
  });

  const areasList = areasQuery.data?.data ?? [];
  const areaNameById = new Map(areasList.map((a) => [a.id, a.name] as const));
  const showAreaFilter = areasQuery.isSuccess && areasList.length > 0;

  const areaFilterOptions: readonly FilterPillOption<string>[] = [
    { label: "Todos", value: ALL_AREAS },
    ...areasList.map((a) => ({ label: a.name, value: a.id })),
  ];

  if (user && !canRead) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="OPERAÇÃO / PRODUTOS" title="Acesso negado">
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              operator ou catalog_operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      {canImport ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setImportOpen(true)}
          title="Importar produtos em lote via planilha (EAN + Nome)"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          Importar
        </Button>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        disabled
        title="Tela de criação ainda não disponível"
      >
        + Novo produto
      </Button>
    </div>
  );

  const columns: DataTableColumn<Product>[] = [
    {
      key: "sku",
      header: "SKU",
      mono: true,
      width: "140px",
      cell: (p) => (
        <span className="text-[var(--color-muted-foreground)]">
          {p.sku ?? "—"}
        </span>
      ),
    },
    {
      key: "name",
      header: "Nome",
      cell: (p) => (
        <Link
          to="/products/$productId"
          params={{ productId: p.id }}
          className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
        >
          {p.name}
        </Link>
      ),
    },
    {
      key: "area_id",
      header: "Área",
      cell: (p) => {
        if (!p.area_id) {
          return <span className="text-[var(--color-muted-foreground)]">—</span>;
        }
        const name = areaNameById.get(p.area_id);
        return (
          <span className="text-[var(--color-muted-foreground)]">
            {name ?? "—"}
          </span>
        );
      },
    },
    {
      key: "image_url",
      header: "Imagem",
      width: "80px",
      cell: (p) =>
        p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            loading="lazy"
            className="h-8 w-8 rounded object-cover"
          />
        ) : (
          <span className="text-[var(--color-muted-foreground)]">—</span>
        ),
    },
    {
      // Preço atual mora em store_prices (por loja). ProductRead não traz —
      // não fazemos N+1 fetches: detalhe mostra histórico completo.
      key: "current_price",
      header: "Preço atual",
      align: "right",
      mono: true,
      cell: () => <span className="text-[var(--color-muted-foreground)]">—</span>,
    },
    {
      key: "updated_at",
      header: "Atualizado em",
      align: "right",
      mono: true,
      cell: (p) => (
        <span className="text-[var(--color-muted-foreground)]">
          {formatDateTime(p.updated_at)}
        </span>
      ),
    },
  ];

  const rows = productsQuery.data?.data ?? [];
  const total = productsQuery.data?.meta.total ?? 0;
  const subtitle = productsQuery.data
    ? `${total} ${total === 1 ? "produto" : "produtos"}`
    : undefined;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / PRODUTOS"
        title="Produtos"
        actions={headerActions}
      >
        <div className="mx-auto max-w-6xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Catálogo do tenant. Use a busca ou o filtro por área para
            encontrar um item.
          </p>

          <div className="mt-6 flex flex-col gap-3">
            <div className="max-w-md">
              <Input
                type="search"
                placeholder="Buscar por nome, SKU ou EAN…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Buscar produtos"
              />
            </div>
            {showAreaFilter ? (
              <FilterPills
                options={areaFilterOptions}
                value={areaId}
                onChange={setAreaId}
              />
            ) : null}
          </div>

          {productsQuery.error ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar produtos
            </div>
          ) : null}

          <div className="mt-4">
            <DataTable<Product>
              columns={columns}
              rows={rows}
              title="Produtos"
              subtitle={subtitle}
              rowKey={(p) => p.id}
              emptyMessage={
                productsQuery.isLoading
                  ? "carregando…"
                  : q.trim() || areaId !== ALL_AREAS
                    ? "nenhum produto bate com o filtro"
                    : "nenhum produto cadastrado"
              }
            />
          </div>

          {productsQuery.data && total > PER_PAGE ? (
            <div className="mt-4">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>

        <ProductsImportModal
          open={importOpen}
          onOpenChange={setImportOpen}
          onImported={() => {
            // Invalida toda a árvore de products (todas as combinações
            // page/q/area) — o import pode ter mudado várias páginas.
            void queryClient.invalidateQueries({ queryKey: ["products"] });
          }}
        />
      </AppLayout>
    </RequireAuth>
  );
}
